import React, { useEffect, useRef, useState } from 'react';
import { FiMenu, FiX } from 'react-icons/fi';
import classNames from 'classnames';
import ClientOnlyPortal from 'src/components/ClientOnlyPortal';
import { useRouter } from 'next/router';
import { scrollTo } from 'src/helpers';
import { Blog, BlogType } from 'src/types';
import TodoImg from 'public/images/blog/todo.png';
import ImageWithFallback from '../ImageWithFallback';
import { useTranslations } from 'next-intl';

const HEADER_HEIGHT = 110;

type Props = {
  blog?: Blog;
  blogType: BlogType,
  h2Eles: SummaryItem[];
  h2Children?: { [key: string]: SummaryItem[] };
  title?: string;
};

export type SummaryItem = {
  id: string;
  ele: HTMLElement;
  tag: string;
  position: number;
  active: boolean;
};

export const SummaryList: React.FC<Props & { onClick?: () => void; containerClass?: string }> = ({
  blog,
  blogType,
  h2Eles,
  h2Children,
  onClick,
  containerClass,
  title
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const translation = useTranslations('components.blog.MobileSummaryButton')

  useEffect(() => {
    const scrollList = () => {
      if (containerClass) {
        const container = document.querySelector('.js-summary-list')
        if (container instanceof HTMLDivElement) {
          const currentEle = container.querySelector('.current')
          if (currentEle instanceof HTMLDivElement) {
            container.scrollTop = currentEle.offsetTop - container.offsetTop
          }
        }
      }
    }

    document.addEventListener('scroll', scrollList)

    return () => {
      document.removeEventListener('scroll', scrollList)
    }
  }, [containerClass])

  return (
    <>
      <div className="tw-relative tw-mb-4">
        <div className="tw-flex tw-items-center tw-font-styleSans tw-font-semibold tw-text-2xl md:tw-text-2xl tw-mb-4">
          <ImageWithFallback
            src={TodoImg}
            alt=""
            className="tw-inline-block tw-static tw-h-8 tw-w-auto tw-object-contain tw-mr-2"
          />
          {title ? title : translation('yourSummary')}
        </div>
      </div>
      <div ref={containerRef} className={`js-summary-list ${containerClass || ''}`}>
        {h2Eles.map((h2Ele, index) => (
          <div key={h2Ele.id} className={`tw-text-brand-navy`}>
            {index > 0 ? (
              <div className={classNames('tw-my-4', {
                'tw-border-t tw-border-grey-lighter': h2Children
              })}></div>
            ) : null}
            <div
              className={classNames('tw-cursor-pointer', {
                'tw-font-semibold current': h2Ele.active,
              })}
              onClick={() => {
                scrollTo(h2Ele.ele, -HEADER_HEIGHT);
                onClick && onClick();
              }}
            >
              {h2Ele.ele.textContent}
            </div>
            {h2Children && h2Children[h2Ele.id]?.length > 0 ? (
              <div className="tw-pl-4">
                {h2Children[h2Ele.id].map((h3Ele, childIndex) => (
                  <div
                    key={h3Ele.id}
                    className={classNames('tw-mt-2 tw-cursor-pointer', {
                      'tw-font-semibold current': h3Ele.active,
                    })}
                    onClick={() => {
                      scrollTo(h3Ele.ele, -HEADER_HEIGHT);
                      onClick && onClick();
                    }}
                  >
                    {['alist', 'list'].includes(blogType) ? `${h3Ele.position + 1}. ` : ''}
                    {h3Ele.ele.textContent}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </>
  );
};

const MobileSummaryButton: React.ReactFCWithChildren<Props> = ({ h2Eles, h2Children, blog, blogType, title }) => {
  const [showSidenav, setShowSidenav] = useState(false);
  const router = useRouter();
  const translation = useTranslations('components.blog.MobileSummaryButton')
  const close = () => {
    setShowSidenav(false);
  };

  useEffect(() => {
    if (router.isReady) {
      const toggle = () => {
        setShowSidenav(false);
      };
      router.events.on('routeChangeComplete', toggle);

      return () => {
        router.events.off('routeChangeComplete', toggle);
      };
    }
  }, [router]);

  return (
    <>
      <div
        className="tw-bg-brand-navy tw-fixed tw-right-0 tw-bottom-12 tw-block md:tw-hidden tw-cursor-pointer tw-py-2 tw-px-2.5 tw-rounded-tl-2xl tw-rounded-bl-2xl tw-text-brand-green tw-z-100"
        style={{ boxShadow: '0 0 4px 0 rgba(0,0,0,.12)' }}
        onClick={() => {
          setShowSidenav(true);
        }}
      >
        <FiMenu className="tw-mr-1 tw-align-topt tw-pb-0.5 tw-text-brand-green" size={24} />
        <span className="tw-leading-none tw-font-styleSans tw-font-semibold tw-text-brand-green tw-lowercase">{title ? title : translation('summary')}</span>
      </div>
      <ClientOnlyPortal selector="#modal-sidenav">
        <div
          className={classNames('sidenav sidenav-rtl', { active: showSidenav })}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            if (e.target instanceof HTMLElement) {
              if (
                e.target.closest('.sidenav__content') ||
                e.target.classList.contains('sidenav__content')
              ) {
                return;
              } else {
                close();
              }
            }
          }}
        >
          <div className="sidenav__inner">
            <div className="sidenav__btn" onClick={close}>
              <FiX className="tw-text-white" size={28} />
            </div>
            <div className="sidenav__content">
              <div className={`tw-px-4 tw-py-8`}>
                <SummaryList
                  blog={blog}
                  blogType={blogType}
                  h2Eles={h2Eles}
                  h2Children={h2Children}
                  onClick={() => setShowSidenav(false)}
                  title={title}
                />
              </div>
            </div>
          </div>
        </div>
      </ClientOnlyPortal>
    </>
  );
};

export default MobileSummaryButton;
